<template>
  <Modal :title="`New ${STRATEGY_PLAY_LABEL}`" :max-width="560" @close="emit('close')">
    <div class="newStrategyForm">
      <label class="newStrategyField">
        <span class="newStrategyLabel">Name</span>
        <input
          class="newStrategyInput"
          type="text"
          v-model="name"
          placeholder="e.g. Series B sales leaders"
          maxlength="200"
        />
      </label>
      <StrategyBriefInput v-model="brief" :disabled="saving" />
      <p v-if="error" class="newStrategyError">{{ error }}</p>
    </div>
    <template #footer>
      <button class="btn secondary" type="button" @click="emit('close')">Cancel</button>
      <button class="btn primary" type="button" :disabled="!name.trim() || !brief.trim() || saving" @click="create">
        {{ saving ? 'Creating…' : 'Create and run' }}
      </button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import Modal from './Modal.vue'
import StrategyBriefInput from '../sales-play/strategy/StrategyBriefInput.vue'
import { STRATEGY_PLAY_LABEL } from '../composables/usePlayEngine'
import { createStrategyPlay } from '../../r3/services/strategyApi'

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'created', play: { id: string; name: string }): void
}>()

const name = ref('')
const brief = ref('')
const saving = ref(false)
const error = ref('')

async function create() {
  if (!name.value.trim() || !brief.value.trim() || saving.value) return
  saving.value = true
  error.value = ''
  try {
    const resp = await createStrategyPlay(name.value.trim(), brief.value.trim())
    emit('created', resp.play)
  } catch (e: any) {
    error.value = e?.message || 'Failed to create the play'
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.newStrategyForm {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}
.newStrategyField {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}
.newStrategyLabel {
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  color: var(--color-text-secondary);
}
.newStrategyInput {
  height: 36px;
  padding: 0 var(--space-3);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-md);
  background: var(--color-bg-app);
  color: var(--color-text-primary);
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
}
.newStrategyInput:focus {
  outline: none;
  border-color: var(--color-accent);
}
.newStrategyError {
  margin: 0;
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  color: var(--color-danger);
}
</style>
